/**
 * API Documentation page content — 100% fidelity to content/developers/api-documentation.md
 * Cross-reference: content/developers/api-documentation.md
 */
import {
  Code2,
  KeyRound,
  Gauge,
  Terminal,
  Layers,
  FileJson,
  AlertTriangle,
  Webhook,
  Package,
  Plug,
  ShieldCheck,
  Globe,
  Cpu,
  Database,
  Send,
  Activity,
  Crosshair,
  Server,
  Search,
  Eye,
} from "lucide-react";
import type { PageData } from "@/components/sections/page-composer";

export const apiDocsMeta = {
  title: "API Documentation | Perception X2 Developer Guide",
  description:
    "Complete API documentation for Perception X2. REST, WebSocket, gRPC, and Webhooks. 500+ endpoints, OAuth 2.0, rate limiting, and code examples.",
  slug: "developers/api-documentation",
  canonical: "https://perception.ac/developers/api-documentation",
};

export const apiDocsData: PageData = {
  hero: {
    eyebrow: "Every Endpoint. Every Detail. Zero Ambiguity.",
    title: "API Documentation.",
    titleAccent: "Precision Built In.",
    lede: "The Perception X2 API documentation provides complete reference material for every protocol, endpoint, authentication flow, and data model available on the platform. Built for developers who need precision, not guesswork.",
    trustLine: "500+ Endpoints · REST / WebSocket / gRPC / Webhooks · <120ms Response Time",
    ctaPrimary: { label: "Explore Endpoints", href: "#api-surface-area" },
    ctaSecondary: { label: "View Code Examples", href: "#code-samples" },
    trustIndicators: [
      "REST / WebSocket / gRPC / Webhooks",
      "OAuth 2.0 + mTLS Authentication",
      "<120ms Average Response Time",
      "99.9999% Uptime SLA",
    ],
    primaryAccent: "var(--color-crimson)",
    secondaryAccent: "var(--color-purple)",
  },
  executiveSummary: {
    eyebrow: "Documentation That Matches the API Itself",
    title: "Comprehensive Reference for Every Protocol",
    intro:
      "Perception X2 is an autonomous perception amplification platform developed by CryptoMize. The API documentation serves as the definitive reference for every interaction with the platform.",
    paragraphs: [
      "This documentation covers four connection protocols — REST for standard operations, WebSocket for real-time streaming, gRPC for high-performance binary communication, and Webhooks for event-driven notifications. Each protocol section includes endpoint specifications, request and response schemas, authentication requirements, and working code examples in Python, JavaScript/TypeScript, and Go.",
      "Whether you are building a sentiment analysis pipeline, configuring crisis detection webhooks, deploying narratives across multiple platforms, or constructing real-time analytics dashboards, this documentation provides the technical depth to move from concept to production without ambiguity. Every endpoint is documented with its HTTP method, path, parameters, request body schema, response schema, error codes, rate limit impact, and authentication requirements.",
      "The Perception X2 API exposes 500+ endpoints across eight functional domains, each designed for specific integration patterns. This page maps those domains, explains the protocols that carry them, and provides the reference material needed to build robust integrations.",
    ],
    metrics: [
      { value: "500+", label: "Endpoints", icon: Layers, accent: "var(--color-crimson)" },
      { value: "8", label: "Functional Domains", icon: Globe, accent: "var(--color-purple)" },
      { value: "<120ms", label: "Response Time", icon: Gauge, accent: "var(--color-cyan)" },
      { value: "4", label: "Protocols", icon: Plug, accent: "var(--color-emerald)" },
      { value: "99.9999%", label: "Uptime SLA", icon: ShieldCheck, accent: "var(--color-amber)" },
    ],
    cta: { label: "Read the Full API Reference", href: "#api-surface-area" },
  },
  problem: {
    eyebrow: "The Integration Challenge",
    title: "Reference Material Developers Actually Use",
    lede: "Most API documentation tells you what an endpoint does. Perception X2 documentation shows you how to build with it — production-ready patterns, error handling, and deployment strategies.",
    accent: "var(--color-cyan)",
    pillars: [
      {
        title: "Complete Endpoint Catalog",
        body: "Every REST, WebSocket, gRPC, and Webhook endpoint documented with method, path, parameters, request/response schemas, and error codes. No undocumented behavior, no assumed knowledge.",
        icon: Layers,
        accent: "var(--color-crimson)",
      },
      {
        title: "Multi-Protocol Reference",
        body: "Side-by-side documentation for REST, WebSocket, gRPC, and Webhooks. Each protocol section explains when to use it, how to connect, and what data formats to expect.",
        icon: Plug,
        accent: "var(--color-purple)",
      },
      {
        title: "Authentication Flows",
        body: "Step-by-step documentation for OAuth 2.0 client credentials, authorization code grants, API key authentication, and mTLS. Token lifecycle management, scope definitions, and security best practices covered in depth.",
        icon: KeyRound,
        accent: "var(--color-cyan)",
      },
      {
        title: "Working Code Examples",
        body: "Copy-paste-ready code in Python, JavaScript/TypeScript, and Go for every major API operation. Examples include error handling, retry logic, and production-ready patterns — not just the happy path.",
        icon: Code2,
        accent: "var(--color-emerald)",
      },
      {
        title: "Rate Limit and Quota Reference",
        body: "Tier-based rate limit tables, header documentation, 429 response handling strategies, and optimization techniques. Every endpoint's rate limit impact is documented.",
        icon: Gauge,
        accent: "var(--color-amber)",
      },
      {
        title: "Error Catalog",
        body: "Structured error response format, complete error code table, resolution guidance for every error condition, and retry strategy recommendations.",
        icon: AlertTriangle,
        accent: "var(--color-denim)",
      },
      {
        title: "Versioning and Migration",
        body: "Semantic versioning policy, deprecation timelines, breaking change notification process, and migration guides for every major version transition. The API maintains a 2-year deprecation window.",
        icon: Terminal,
        accent: "var(--color-violet)",
      },
      {
        title: "Data Model Specifications",
        body: "JSON and Protocol Buffer schema definitions for every request and response object. Field types, validation rules, required versus optional fields, and enum values are documented for all data models.",
        icon: FileJson,
        accent: "var(--color-rose)",
      },
    ],
  },
  solution: {
    eyebrow: "The Documentation",
    title: "Four Protocols, One Comprehensive Reference",
    intro: "REST for standard operations. WebSocket for real-time streaming. gRPC for high-throughput binary communication. Webhooks for event-driven notifications. All documented with production-ready patterns and working code samples.",
    accent: "var(--color-crimson)",
    pillarAccents: ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)"],
    pillars: [
      {
        title: "REST API",
        body: "Standard CRUD operations, queries, and batch processing. JSON payloads with consistent response schemas. <120ms response time. 80-120ms average latency across 500+ endpoints.",
        icon: Terminal,
      },
      {
        title: "WebSocket Streams",
        body: "Real-time event streaming with sub-200ms delivery. Channel subscriptions, filters, and event replay. Persistent bidirectional connections for live dashboards and monitoring.",
        icon: Activity,
      },
      {
        title: "gRPC Services",
        body: "High-throughput binary communication with Protocol Buffers. Bidirectional streaming. 30-80ms average latency. Persistent multiplexed connections for performance-critical integrations.",
        icon: Cpu,
      },
      {
        title: "Webhook Events",
        body: "Event-driven notifications with HMAC-SHA256 signatures. Configurable retry with exponential backoff. At-least-once delivery. Event payload includes full context and delivery identifiers.",
        icon: Webhook,
      },
    ],
    cta: { label: "Explore Endpoints", href: "#api-surface-area" },
  },
  howItWorks: {
    eyebrow: "Quick Start",
    title: "From Zero to First API Call in Five Steps",
    accent: "var(--color-cyan)",
    stepAccents: ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)", "var(--color-amber)"],
    steps: [
      { number: "01", title: "Obtain API Credentials", body: "Navigate to the Perception X2 Developer Portal and create a project. You will receive a client_id and client_secret for OAuth 2.0 authentication. Store these securely — the client secret is not retrievable after creation." },
      { number: "02", title: "Install an SDK", body: "Choose your language and install the official SDK: pip install perception-sdk (Python), npm install @cryptomize/perception-sdk (JavaScript), or go get github.com/CryptoMize/perception-sdk-go (Go)." },
      { number: "03", title: "Authenticate", body: "Exchange your credentials for an access token. Token is obtained automatically on first API call when using an SDK. Manual token exchange available via POST /oauth/token." },
      { number: "04", title: "Make Your First API Call", body: "Query real-time sentiment data for a specific narrative. The SDK abstracts protocol negotiation, rate limiting, and error recovery." },
      { number: "05", title: "Verify the Response", body: "A successful response returns a structured JSON object with sentiment scores, confidence levels, and emotional dimension analysis. Compare against documented schema." },
    ],
  },
  coreFeatures: {
    eyebrow: "Core Features",
    title: "API Reference Capabilities",
    accent: "var(--color-emerald)",
    featureAccents: ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)", "var(--color-amber)", "var(--color-denim)"],
    features: [
      { number: "01", title: "Complete Endpoint Catalog", body: "Every REST, WebSocket, gRPC, and Webhook endpoint documented with method, path, parameters, request/response schemas, and error codes.", icon: Layers },
      { number: "02", title: "OAuth 2.0 Authentication", body: "Client credentials, authorization code grants, and mTLS. Scoped permissions, automatic token refresh, and audit logging built in.", icon: KeyRound },
      { number: "03", title: "Working Code Examples", body: "Python, JavaScript/TypeScript, and Go code samples for every major operation. Production patterns with error handling and retry logic.", icon: Code2 },
      { number: "04", title: "Rate Limit Documentation", body: "Tier-based rate limit tables, header reference, 429 response handling, and optimization strategies. Every endpoint's rate limit impact documented.", icon: Gauge },
      { number: "05", title: "Error Code Reference", body: "Structured error response format, complete error code table, resolution guidance, and retry strategy recommendations.", icon: AlertTriangle },
      { number: "06", title: "Data Model Schemas", body: "JSON and Protocol Buffer schema definitions for every request and response object. Field types, validation rules, and enum values documented.", icon: FileJson },
    ],
  },
  useCases: {
    eyebrow: "Real-World Integration Scenarios",
    title: "What Developers Build With the API",
    accent: "var(--color-emerald)",
    cases: [
      {
        title: "Sentiment Monitoring Dashboard",
        body: "Build a real-time dashboard that displays sentiment scores across multiple platforms. Use WebSocket streaming for live updates and REST batch endpoints for historical trend data. Integration time: 2-4 hours with the Python SDK.",
        metric: "2-4 Hours",
        icon: Gauge,
        accent: "var(--color-crimson)",
      },
      {
        title: "Automated Crisis Response System",
        body: "Configure webhooks to detect crisis events and trigger automated response workflows. When crisis events arrive at your endpoint, execute predefined playbooks — activate counter-narratives, notify stakeholders, and begin monitoring escalation patterns.",
        metric: "Real-Time",
        icon: AlertTriangle,
        accent: "var(--color-amber)",
      },
      {
        title: "Multi-Platform Content Deployment Pipeline",
        body: "Integrate the Perception X2 deployment API into your content management workflow. Deploy narratives to 50+ platforms through a single API call, track deployment status in real time, and receive webhook notifications on completion or failure.",
        metric: "50+ Platforms",
        icon: Layers,
        accent: "var(--color-cyan)",
      },
      {
        title: "Competitive Intelligence Aggregation",
        body: "Use the Intelligence domain endpoints to aggregate competitive data across platforms. Schedule periodic batch queries, store results in your analytics infrastructure, and generate comparative reports through the Analytics domain.",
        metric: "Scheduled",
        icon: Globe,
        accent: "var(--color-emerald)",
      },
      {
        title: "Embedded Perception API",
        body: "Embed sentiment analysis capabilities into your own customer-facing products. Use the Sentiment domain API to provide real-time text analysis, emotional dimension scoring, and platform-specific sentiment breakdowns as a feature of your application.",
        metric: "Embedded",
        icon: Code2,
        accent: "var(--color-purple)",
      },
    ],
  },
  benefits: {
    eyebrow: "Benefits",
    title: "Why Developers Choose This Documentation",
    accent: "var(--color-crimson)",
    benefits: [
      { title: "Zero Ambiguity", body: "Every endpoint documented with method, path, parameters, request/response schemas, and error codes. No guesswork required during integration.", icon: ShieldCheck, accent: "var(--color-crimson)" },
      { title: "Production-Ready Patterns", body: "Code examples include error handling, retry logic, and pagination — not just the happy path. Patterns that ship to production.", icon: Code2, accent: "var(--color-purple)" },
      { title: "Versioned Alongside the API", body: "Documentation is versioned with the API itself. Developers working with a specific version see reference material that matches exactly.", icon: Terminal, accent: "var(--color-cyan)" },
      { title: "Multi-Language Coverage", body: "Working code in Python, JavaScript/TypeScript, and Go. Every example in every language — no translation work required.", icon: Package, accent: "var(--color-emerald)" },
      { title: "Complete Schemas", body: "JSON and Protocol Buffer schema definitions for every request and response. Field types, validation rules, and enum values are documented for all data models.", icon: FileJson, accent: "var(--color-amber)" },
      { title: "Operational Excellence", body: "Rate limit headers, error codes, retry strategies, and webhook event catalogs — operational concerns documented as thoroughly as functional concerns.", icon: Webhook, accent: "var(--color-denim)" },
    ],
  },
  usps: {
    eyebrow: "Why This Documentation",
    title: "What Makes the Perception X2 API Reference Different",
    intro: "Most API documentation tells you what an endpoint does. Perception X2 documentation shows you how to build with it — production-ready patterns, error handling, and deployment strategies.",
    accent: "var(--color-amber)",
    usps: [
      { title: "500+ Documented Endpoints", body: "Every REST, WebSocket, gRPC, and Webhook endpoint documented with method, path, parameters, schemas, and error codes.", icon: Layers, accent: "var(--color-crimson)" },
      { title: "Production-Tested Code", body: "Code examples include error handling, retry logic, and pagination. Not just the happy path — patterns that ship to production.", icon: Code2, accent: "var(--color-purple)" },
      { title: "Four Protocols, One Schema", body: "REST, WebSocket, gRPC, and Webhooks share unified data models. Migrate between protocols without changing business logic.", icon: Plug, accent: "var(--color-cyan)" },
      { title: "99.9999% Uptime SLA", body: "Tier-based rate limits, automatic retry, and circuit breaker patterns documented. Operational concerns covered as thoroughly as functional concerns.", icon: Gauge, accent: "var(--color-emerald)" },
      { title: "Versioned Reference", body: "Documentation is versioned alongside the API. Developers working with a specific version see reference material that matches exactly.", icon: Terminal, accent: "var(--color-amber)" },
      { title: "Enterprise Security", body: "OAuth 2.0, mTLS, scope-based authorization, and audit logging. Security documentation meets enterprise compliance requirements.", icon: ShieldCheck, accent: "var(--color-denim)" },
    ],
    cta: { label: "View Full Reference", href: "#api-surface-area" },
  },
  whoItsFor: {
    eyebrow: "Who This Documentation Serves",
    title: "Built for Engineers Building Production Systems",
    accent: "var(--color-denim)",
    segments: [
      { title: "Backend Engineers", body: "Building server-side integrations, automation pipelines, and data processing systems that consume Perception X2 APIs.", icon: Server, accent: "var(--color-crimson)" },
      { title: "Frontend Developers", body: "Constructing dashboards, monitoring interfaces, and real-time visualizations powered by WebSocket streams and REST queries.", icon: Activity, accent: "var(--color-purple)" },
      { title: "Platform Engineers", body: "Integrating Perception X2 into existing technology stacks — CRM systems, marketing platforms, analytics infrastructure.", icon: Database, accent: "var(--color-cyan)" },
      { title: "Data Engineers", body: "Building batch processing pipelines, analytics warehouses, and intelligence aggregation systems using gRPC streaming.", icon: Gauge, accent: "var(--color-emerald)" },
      { title: "DevOps Teams", body: "Configuring webhook receivers, building CI/CD integrations, and monitoring production deployments with event-driven automation.", icon: Webhook, accent: "var(--color-amber)" },
      { title: "Security Teams", body: "Implementing OAuth 2.0 flows, mTLS certificate management, and scope-based access controls for enterprise-grade security.", icon: ShieldCheck, accent: "var(--color-denim)" },
    ],
  },
  whyChooseUs: {
    eyebrow: "Why Perception X2",
    title: "The Standard for Perception Intelligence APIs",
    accent: "var(--color-violet)",
    reasons: [
      { title: "15+ Years of API Excellence", body: "Every endpoint refined through thousands of real-world deployments across the world's most demanding environments.", icon: Crosshair, accent: "var(--color-crimson)" },
      { title: "300+ Elite Client Integrations", body: "Trusted by governments, global enterprises, and institutions across multiple continents. Each engagement contributes to API refinement.", icon: Globe, accent: "var(--color-purple)" },
      { title: "Zero Security Incidents", body: "Your most sensitive API data protected by military-grade security. Zero incidents in 15+ years of operations.", icon: ShieldCheck, accent: "var(--color-cyan)" },
      { title: "Verified Performance", body: "<120ms average response time, 99.9999% uptime, 50,000+ concurrent connections. These are verified operational metrics.", icon: Gauge, accent: "var(--color-emerald)" },
      { title: "Multi-Protocol Support", body: "REST, WebSocket, gRPC, and Webhooks from a unified API surface. Choose the protocol that matches your integration requirements.", icon: Send, accent: "var(--color-amber)" },
      { title: "Enterprise Authentication", body: "OAuth 2.0 with scoped permissions, mTLS for high-security deployments, automatic token refresh, and comprehensive audit logging.", icon: KeyRound, accent: "var(--color-denim)" },
    ],
  },
  related: {
    eyebrow: "Related Resources",
    title: "Continue Building with Perception X2",
    accent: "var(--color-cyan)",
    capabilities: [
      { name: "SDK & Libraries", href: "/developers/sdk", body: "Official client libraries for Python, JavaScript, Go, and more.", icon: Package, accent: "var(--color-crimson)" },
      { name: "Code Examples", href: "/developers/code-examples", body: "Production-ready code samples in every supported language.", icon: Code2, accent: "var(--color-purple)" },
      { name: "Integration Guides", href: "/developers/integration-guides", body: "Step-by-step tutorials from first API call to production.", icon: Layers, accent: "var(--color-cyan)" },
      { name: "Webhooks & Events", href: "/developers/webhooks", body: "Event-driven architecture, payload schemas, and retry policies.", icon: Webhook, accent: "var(--color-emerald)" },
    ],
    engines: [],
    services: [],
  },
  faq: {
    eyebrow: "Frequently Asked Questions",
    title: "API Documentation — Common Developer Questions",
    accent: "var(--color-cyan)",
    items: [
      { q: "What protocols does the Perception X2 API support?", a: "The API supports four protocols: REST for standard CRUD operations and queries, WebSocket for real-time event streaming, gRPC for high-performance binary communication, and Webhooks for event-driven notifications. All four share unified OAuth 2.0 authentication and consistent data schemas." },
      { q: "How do I authenticate API requests?", a: "Perception X2 uses OAuth 2.0 for authentication. Client credentials grants handle server-to-server integrations, while authorization code grants support user-delegated access. API keys are available for development and testing with restricted scope. All authentication traffic is encrypted with TLS 1.3." },
      { q: "What are the rate limits for the API?", a: "Rate limits vary by tier: Free (100 requests/minute), Starter (500), Professional (1,000), Business (5,000), and Enterprise (10,000). Rate limit headers are included in every response, and the API returns HTTP 429 with a Retry-After header when limits are exceeded. Batch requests count as a single request." },
      { q: "Is there a sandbox environment for testing?", a: "Yes. The sandbox environment provides the full API surface with sample data and reduced rate limits. Developers can test all integrations — REST, WebSocket, gRPC, and Webhooks — against realistic data without affecting production deployments." },
      { q: "How does API versioning work?", a: "Perception X2 uses semantic versioning. The current version is v2. Deprecated endpoints are maintained for 2 years after deprecation notice. Version migration guides are published with every major release. New fields are always additive — existing integrations do not break when new features are added." },
      { q: "What happens when an API request fails?", a: "The API returns structured error responses with error codes, human-readable messages, and request identifiers. Common errors include actionable guidance for resolution. All errors are logged server-side and accessible through the developer portal." },
      { q: "How do I handle real-time sentiment data?", a: "Use the WebSocket API for real-time sentiment streaming, or configure Webhooks for threshold-based notifications. The WebSocket API supports channel subscriptions with platform, severity, and metric filters. Event replay ensures no data is lost during connection interruptions." },
      { q: "Can I migrate from REST to gRPC without rewriting my integration?", a: "Yes. The REST and gRPC APIs share identical data schemas defined in Protocol Buffers. Your data models, field names, and business logic remain unchanged — only the transport layer changes. Official SDKs abstract the protocol, enabling migration with minimal code changes." },
      { q: "How do webhooks differ from WebSocket streams?", a: "Webhooks push events to your configured HTTP endpoint when conditions are met — ideal for automated workflows and alerting. WebSocket streams provide a persistent connection for continuous real-time data — ideal for dashboards and live monitoring. Both support the same event types and data schemas." },
      { q: "Is the API compliant with SOC 2 and ISO 27001?", a: "Yes. The Perception X2 API operates within the platform's SOC 2 Type II and ISO 27001 certified environment. API access logs, authentication events, and data operations are auditable through the developer portal. Scope-based authorization enables least-privilege access control." },
    ],
  },
  paa: {
    eyebrow: "People Also Ask",
    title: "API Documentation — What Developers Ask",
    accent: "var(--color-emerald)",
    items: [
      { q: "How do I get started with the Perception X2 API?", a: "Create a developer account at the Perception X2 Developer Portal, obtain OAuth 2.0 client credentials, install an official SDK (Python, JavaScript, or Go), and execute your first authenticated API call. The Quick Start guide walks through every step with working code samples." },
      { q: "What is the difference between REST and gRPC APIs?", a: "REST uses JSON over HTTP for standard CRUD operations and queries. gRPC uses Protocol Buffers over HTTP/2 for high-throughput, low-latency binary communication. Both protocols share identical data schemas, so you can migrate between them without changing your business logic." },
      { q: "How can I get an API key?", a: "Generate API keys through the Developer Portal after creating your account. API keys are intended for development and testing with restricted scope. Production deployments should use OAuth 2.0 client credentials for enhanced security and granular permission control." },
      { q: "Does the API support batch operations?", a: "Yes. The /v2/batch endpoint executes up to 100 operations in a single request while counting as one call against the rate limit. This is significantly more efficient than individual API calls for bulk operations." },
      { q: "What happens to my integration when APIs are deprecated?", a: "Deprecated endpoints are maintained for 2 years after deprecation notice. You receive notification 12 months in advance of any breaking change. Migration guides are published with every major version transition, and SDK compatibility layers enable gradual upgrades." },
    ],
  },
  cta: {
    eyebrow: "Start Building",
    title: "Begin Integrating Perception X2",
    intro: "Request API access to start with the sandbox environment, explore the full endpoint catalog, and build your integration with confidence. The developer support team is available for architecture guidance and enterprise onboarding.",
    promises: [
      "Our developer support team is available for architecture guidance, integration planning, and enterprise onboarding.",
      "All integrations begin with the sandbox environment — test against realistic data without affecting production deployments.",
    ],
    ctaPrimary: { label: "Get Your API Key", href: "/#contact" },
    ctaSecondary: { label: "View Full API Reference", href: "#api-surface-area" },
    ctaTertiary: { label: "Install an SDK", href: "/developers/sdk" },
    trustBar: [
      "15+ Years Operational Excellence",
      "300+ Elite Client Integrations",
      "99.9999% Uptime",
      "Zero Security Incidents",
      "500+ Documented Endpoints",
    ],
    accent: "var(--color-crimson)",
    secondaryAccent: "var(--color-purple)",
  },
};
